/* =============================================================
   StudentLayout.jsx — Student portal page shell
   -------------------------------------------------------------
   Wraps every student screen with the student sidebar, a fixed
   top header (search + notifications + avatar), and the main
   content area. Content shifts right with the sidebar width.

   Preview banner:
     When an IO manager is viewing the portal in preview mode
     (?as=studentId), a yellow banner is shown at the very top of
     the page. The header and sidebar shift down by the banner
     height (40px) so nothing is hidden underneath it.
   ============================================================= */

import { useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import StudentSidebar          from './StudentSidebar';
import StudentGlobalSearch     from './StudentGlobalSearch';
import StudentNotificationBell from './StudentNotificationBell';
import StudentPreviewBanner    from './StudentPreviewBanner';
import { useSidebar }          from '../context/SidebarContext';
import { useStudentContext }    from '../hooks/useStudentContext';
import { useAuth }             from '../context/AuthContext';


export default function StudentLayout({ children }) {
  const { open } = useSidebar();
  const { isPreview, student, exitUrl } = useStudentContext();
  const { logout } = useAuth();
  const navigate   = useNavigate();
  const [params]   = useSearchParams();
  const withPreview = (path) => {
    const as = params.get('as');
    return as ? `${path}?as=${as}` : path;
  };

  const [menuOpen, setMenuOpen] = useState(false);
  const leaveTimer = useRef(null);

  const handleMouseEnter = () => {
    clearTimeout(leaveTimer.current);
    setMenuOpen(true);
  };
  const handleMouseLeave = () => {
    leaveTimer.current = setTimeout(() => setMenuOpen(false), 150);
  };

  // When the preview banner is shown, everything shifts down by its height.
  const bannerH = isPreview ? 40 : 0;

  return (
    <div className="min-h-screen bg-[#f8f9ff]">

      {/* Preview banner — fixed to top of viewport, above everything */}
      <StudentPreviewBanner
        isPreview={isPreview}
        studentName={student?.name}
        exitUrl={exitUrl}
      />

      <StudentSidebar topOffset={bannerH} />

      {/* Fixed top header */}
      <div
        className="fixed right-0 h-16 bg-white/80 backdrop-blur-sm border-b border-slate-100 flex items-center px-8 gap-4 z-30 transition-all duration-300"
        style={{
          top:  bannerH,
          left: open ? '280px' : '72px',
        }}
      >
        <StudentGlobalSearch />

        <div className="ml-auto flex items-center gap-2">
          <StudentNotificationBell />

          {/* Avatar + hover dropdown */}
          <div
            className="relative ml-1"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <button className="w-9 h-9 rounded-full bg-sky-500 flex items-center justify-center text-white text-sm font-semibold hover:opacity-90 transition-opacity focus:outline-none">
              {student?.name
                ? student.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
                : '?'}
            </button>

            {menuOpen && (
              <div className="absolute right-0 top-11 w-48 bg-white rounded-xl shadow-lg border border-slate-100 py-1.5 z-50">
                {/* User info */}
                <div className="px-4 py-2.5 border-b border-slate-100">
                  <p className="text-xs font-semibold text-[#0b1c30] truncate">{student?.name ?? '—'}</p>
                  <p className="text-[11px] text-slate-400 truncate mt-0.5">{student?.email ?? ''}</p>
                </div>

                {/* Settings */}
                <button
                  onClick={() => navigate(withPreview('/student/settings'))}
                  className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-[#464555] hover:bg-slate-50 transition-colors"
                >
                  <span className="material-symbols-outlined text-[17px] text-slate-400">settings</span>
                  Settings
                </button>

                {/* Sign out */}
                <button
                  onClick={() => logout()}
                  className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 transition-colors"
                >
                  <span className="material-symbols-outlined text-[17px]">logout</span>
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Page content */}
      <main
        className="min-h-screen transition-all duration-300"
        style={{
          paddingTop:  bannerH + 64, // banner + header
          marginLeft: open ? '280px' : '72px',
        }}
      >
        <div className="p-10">
          {children}
        </div>
      </main>
    </div>
  );
}
